import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Plus, Edit, Trash2, Loader2, X, TrendingUp, FileText, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAffiliateCoupons = () => {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [selectedCouponForReport, setSelectedCouponForReport] = useState(null);
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  
  const [formData, setFormData] = useState({
    code: '',
    affiliate_title: '',
    discount_percentage: '',
    commission_percentage: '',
    is_active: true
  });

  useEffect(() => {
    const isAuth = localStorage.getItem('adminAuth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    fetchCoupons();
  }, [navigate]);

  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API}/admin/affiliate-coupons`);
      setCoupons(response.data);
    } catch (error) {
      console.error('Error fetching affiliate coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingCoupon(null);
    setFormData({
      code: '',
      affiliate_title: '',
      discount_percentage: '',
      commission_percentage: '',
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      affiliate_title: coupon.affiliate_title,
      discount_percentage: coupon.discount_percentage,
      commission_percentage: coupon.commission_percentage || 0,
      is_active: coupon.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCoupon) {
        await axios.put(`${API}/admin/affiliate-coupons/${editingCoupon.id}`, {
          affiliate_title: formData.affiliate_title,
          discount_percentage: parseFloat(formData.discount_percentage),
          commission_percentage: parseFloat(formData.commission_percentage || 0),
          is_active: formData.is_active
        });
      } else {
        await axios.post(`${API}/admin/affiliate-coupons`, {
          code: formData.code.toUpperCase(),
          affiliate_title: formData.affiliate_title,
          discount_percentage: parseFloat(formData.discount_percentage),
          commission_percentage: parseFloat(formData.commission_percentage || 0),
          is_active: formData.is_active
        });
      }
      
      setShowModal(false);
      fetchCoupons();
    } catch (error) {
      console.error('Error saving affiliate coupon:', error);
      alert(error.response?.data?.detail || 'Failed to save affiliate coupon');
    }
  };

  const handleDelete = async (couponId) => {
    if (!confirm('Are you sure you want to delete this affiliate coupon?')) return;
    
    try {
      await axios.delete(`${API}/admin/affiliate-coupons/${couponId}`);
      fetchCoupons();
    } catch (error) {
      console.error('Error deleting coupon:', error);
      alert('Failed to delete affiliate coupon');
    }
  };

  const openReportModal = (coupon) => {
    setSelectedCouponForReport(coupon);
    setReportMonth(new Date().toISOString().slice(0, 7));
    setReportData(null);
    setShowReportModal(true);
  };

  const fetchReport = async () => {
    if (!selectedCouponForReport) return;
    
    setLoadingReport(true);
    try {
      const response = await axios.get(
        `${API}/admin/affiliate-coupons/${selectedCouponForReport.id}/report?month=${reportMonth}&format=json`
      );
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      alert('Failed to fetch report');
    } finally {
      setLoadingReport(false);
    }
  };

  const downloadCSV = () => {
    if (!selectedCouponForReport) return;
    
    const url = `${API}/admin/affiliate-coupons/${selectedCouponForReport.id}/report?month=${reportMonth}&format=csv`;
    window.open(url, '_blank');
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-black">Affiliate Coupons</h1>
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-[#1E88E5] text-white rounded-lg hover:bg-[#1565C0] transition-colors flex items-center space-x-2"
            data-testid="create-affiliate-btn"
          >
            <Plus className="w-5 h-5" />
            <span>Create Affiliate Coupon</span>
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-12 h-12 text-[#1E88E5] animate-spin" />
          </div>
        ) : (
          <>
            {/* Statistics Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-gray-600 text-sm font-medium">Total Coupons</h3>
                  <TrendingUp className="w-5 h-5 text-[#1E88E5]" />
                </div>
                <p className="text-3xl font-bold text-black">{coupons.length}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {coupons.filter(c => c.is_active).length} active
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-gray-600 text-sm font-medium">Total Usage</h3>
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-black">
                  {coupons.reduce((sum, c) => sum + c.usage_count, 0)}
                </p>
                <p className="text-sm text-gray-500 mt-1">times used</p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-gray-600 text-sm font-medium">Total Revenue</h3>
                  <TrendingUp className="w-5 h-5 text-yellow-600" />
                </div>
                <p className="text-3xl font-bold text-black">
                  ${coupons.reduce((sum, c) => sum + c.total_revenue, 0).toFixed(2)}
                </p>
                <p className="text-sm text-gray-500 mt-1">from affiliate sales</p>
              </div>
            </div>

            {/* Coupons Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              {coupons.length === 0 ? (
                <p className="text-gray-600 text-center py-20">No affiliate coupons created yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Code</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Affiliate</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Discount</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Commission</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Usage</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Revenue</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coupons.map((coupon) => (
                        <tr key={coupon.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-mono text-sm font-semibold">{coupon.code}</td>
                          <td className="py-3 px-4 text-sm">{coupon.affiliate_title}</td>
                          <td className="py-3 px-4 text-sm font-semibold text-[#1E88E5]">
                            {coupon.discount_percentage}%
                          </td>
                          <td className="py-3 px-4 text-sm font-semibold text-green-600">
                            {coupon.commission_percentage || 0}%
                          </td>
                          <td className="py-3 px-4 text-sm">{coupon.usage_count}</td>
                          <td className="py-3 px-4 text-sm">${coupon.total_revenue.toFixed(2)}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              coupon.is_active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {coupon.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => openReportModal(coupon)}
                                className="p-2 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                                title="View Report"
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openEditModal(coupon)}
                                className="p-2 text-[#1E88E5] hover:bg-[#E3F2FD] rounded transition-colors"
                                title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(coupon.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-black">
                    {editingCoupon ? 'Edit Affiliate Coupon' : 'Create Affiliate Coupon'}
                  </h2>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-black mb-2">
                      Coupon Code *
                    </label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent uppercase"
                      placeholder="e.g., PARTNER10"
                      required
                      disabled={editingCoupon}
                      data-testid="affiliate-code-input"
                    />
                    {editingCoupon && (
                      <p className="text-xs text-gray-500 mt-1">Coupon code cannot be changed</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-black mb-2">
                      Affiliate Title *
                    </label>
                    <input
                      type="text"
                      value={formData.affiliate_title}
                      onChange={(e) => setFormData({ ...formData, affiliate_title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                      placeholder="e.g., Partner Company"
                      required
                      data-testid="affiliate-title-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-black mb-2">
                      Discount Percentage *
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.discount_percentage}
                        onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                        placeholder="10"
                        required
                        data-testid="discount-percentage-input"
                      />
                      <span className="absolute right-4 top-2.5 text-gray-500">%</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-black mb-2">
                      Commission Percentage *
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.commission_percentage}
                        onChange={(e) => setFormData({ ...formData, commission_percentage: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                        placeholder="15"
                        required
                        data-testid="commission-percentage-input"
                      />
                      <span className="absolute right-4 top-2.5 text-gray-500">%</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Percentage of revenue (after tax) paid to affiliate</p>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 text-[#1E88E5] border-gray-300 rounded focus:ring-[#1E88E5]"
                      data-testid="affiliate-active-checkbox"
                    />
                    <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                      Active (customers can use this coupon)
                    </label>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-[#1E88E5] text-white rounded-lg hover:bg-[#1565C0] transition-colors"
                      data-testid="save-affiliate-btn"
                    >
                      {editingCoupon ? 'Save Changes' : 'Create Coupon'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Report Modal */}
      {showReportModal && selectedCouponForReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-black">
                  Affiliate Commission Report
                </h2>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Affiliate:</strong> {selectedCouponForReport.affiliate_title} ({selectedCouponForReport.code})
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Commission Rate:</strong> {selectedCouponForReport.commission_percentage || 0}%
                </p>
              </div>

              <div className="flex gap-4 mb-6">
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-black mb-2">
                    Select Month
                  </label>
                  <input
                    type="month"
                    value={reportMonth}
                    onChange={(e) => setReportMonth(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5]"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <button
                    onClick={fetchReport}
                    disabled={loadingReport}
                    className="px-6 py-2 bg-[#1E88E5] text-white rounded-lg hover:bg-[#1565C0] transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {loadingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                    Generate Report
                  </button>
                  {reportData && (
                    <button
                      onClick={downloadCSV}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download CSV
                    </button>
                  )}
                </div>
              </div>

              {reportData ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Gift Cards Sold</p>
                      <p className="text-3xl font-bold text-black">{reportData.num_cards_sold}</p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                      <p className="text-3xl font-bold text-green-700">${reportData.total_revenue.toFixed(2)}</p>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">After Tax ({reportData.tax_percentage}%)</p>
                      <p className="text-3xl font-bold text-yellow-700">${reportData.revenue_after_tax.toFixed(2)}</p>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Total Commission</p>
                      <p className="text-3xl font-bold text-purple-700">${reportData.total_commission.toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Orders Table */}
                  {reportData.orders.length > 0 ? (
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-3 border-b">
                        <h3 className="font-semibold text-black">Order Details</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left py-2 px-4 text-sm font-semibold text-gray-700">Order ID</th>
                              <th className="text-left py-2 px-4 text-sm font-semibold text-gray-700">Amount</th>
                              <th className="text-left py-2 px-4 text-sm font-semibold text-gray-700">Date</th>
                              <th className="text-left py-2 px-4 text-sm font-semibold text-gray-700">Recipient</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.orders.map((order, idx) => (
                              <tr key={idx} className="border-b hover:bg-gray-50">
                                <td className="py-2 px-4 text-sm font-mono">{order.order_id.slice(0, 8)}...</td>
                                <td className="py-2 px-4 text-sm font-semibold text-green-600">${order.amount.toFixed(2)}</td>
                                <td className="py-2 px-4 text-sm">{new Date(order.order_date).toLocaleDateString()}</td>
                                <td className="py-2 px-4 text-sm">{order.recipient_email}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <p className="text-gray-500 text-lg">No orders found for this month</p>
                      <p className="text-gray-400 text-sm mt-2">Try selecting a different month</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg mb-2">No report generated yet</p>
                  <p className="text-gray-400 text-sm">Select a month and click "Generate Report" to view affiliate commission details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminAffiliateCoupons;
